import * as THREE from 'three';
import { SKY_TRANS_GLSL } from './sky';

// The air between the eye and a body that is a point of light at infinity (a star, the Milky Way): one reading for all of them, the sky-view table's transmittance (sky.ts, skyView.ts).
//
// The table holds, for the eye where it is (its height above the sea) and for every direction, the transmittance of the physical atmosphere (Rayleigh, aerosol, ozone) along the view ray to where it
// ends: the top of the air, or the ground. That is `trans(r, mu)` along the body's own direction, for an eye on the ground, on a mountain or in a balloon, with nothing here that knows which it is.
// The table is laid out from the eye's TRUE horizon (the ray that grazes the planet, elevation `uElH`, below zero from height), so a body is hidden where the planet covers it: below that
// elevation, not below a fixed one. Where the table is off (the eye is at another world, whose air the client does not draw) there is no air to dim by: a sphere in the way hides the body.
//
// The scintillation of a star grows with the path its light takes through the air; that is read back from the transmittance (the optical depth in green over a typical zenith depth), so it
// follows the air of the place and the weather and not a table of airmasses made for a flat ground.
const TAU_GREEN_ZENITH = 0.17;       // a clean zenith's optical depth in green (Rayleigh 0.1, aerosol 0.04, ozone 0.03): one air mass
const AIRMASS_MAX = 40;              // the horizon's

export const BODY_AIR_GLSL = /* glsl */ `
${SKY_TRANS_GLSL}
uniform float uSVOn;         // 1 when the table is the eye's (the home planet's air)
vec3 bodyTrans(vec3 d) { return uSVOn > 0.5 ? svTrans(d) : vec3(1.0); }
bool belowTrueHorizon(vec3 d) { return uSVOn > 0.5 && asin(clamp(d.y, -1.0, 1.0)) < uElH; }
float airmassOf(vec3 t) { return clamp(-log(max(t.g, 1e-4)) / ${TAU_GREEN_ZENITH.toFixed(3)}, 0.0, ${AIRMASS_MAX.toFixed(1)}); }
`;

/** The uniforms BODY_AIR_GLSL reads. */
export function bodyAirUniforms() {
  return { uSVT: { value: null as THREE.Texture | null }, uElH: { value: 0 }, uSVOn: { value: 0 } };
}

export type BodyAirUniforms = ReturnType<typeof bodyAirUniforms>;

/** The sky-view transmittance (Sky.transTexture, or null while the table is not the eye's) and the elevation of the true horizon it was laid out for (Sky.horizonElevation). */
export function setBodyAir(u: BodyAirUniforms, tex: THREE.Texture | null, elH: number) {
  u.uSVT.value = tex; u.uElH.value = Math.min(0, elH); u.uSVOn.value = tex ? 1 : 0;
}
